import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { pushNotificationService } from '../../../services/pushNotificationService';

const NotificationSettingsSection = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState({
    email: {
      dailyReminders: true,
      weeklyReports: true,
      achievements: true,
      goalUpdates: false,
      tips: true
    },
    push: {
      waterReminders: true,
      activityReminders: true,
      sleepReminders: true,
      mealReminders: false,
      achievements: true,
      duelEvents: true
    }
  });

  const [emailDigestSettings, setEmailDigestSettings] = useState({
    emailDigestEnabled: true,
    weeklyDigestDay: 'Monday',
    mealRemindersEnabled: false,
    breakfastReminderTime: '08:00',
    lunchReminderTime: '12:30',
    dinnerReminderTime: '18:30',
    achievementEmailsEnabled: true
  });

  const [emailSummarySettings, setEmailSummarySettings] = useState({
    emailBadges: false,
    emailMilestones: false,
    emailDuels: false,
    emailFriendActivity: false
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);

  useEffect(() => {
    loadEmailDigestSettings();
    checkPushNotificationStatus();
  }, [user]);

  const checkPushNotificationStatus = async () => {
    const supported = pushNotificationService?.isSupported();
    setPushSupported(supported);

    if (supported) {
      const subscribed = await pushNotificationService?.isSubscribed();
      setPushSubscribed(subscribed);
    }
  };

  const loadEmailDigestSettings = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('email_digest_enabled, weekly_digest_day, meal_reminders_enabled, breakfast_reminder_time, lunch_reminder_time, dinner_reminder_time, achievement_emails_enabled, email_badges, email_milestones, email_duels, email_friend_activity')
        ?.eq('id', user?.id)
        ?.single();

      if (error) throw error;

      if (data) {
        setEmailDigestSettings({
          emailDigestEnabled: data?.email_digest_enabled ?? true,
          weeklyDigestDay: data?.weekly_digest_day || 'Monday',
          mealRemindersEnabled: data?.meal_reminders_enabled ?? false,
          breakfastReminderTime: data?.breakfast_reminder_time?.substring(0, 5) || '08:00',
          lunchReminderTime: data?.lunch_reminder_time?.substring(0, 5) || '12:30',
          dinnerReminderTime: data?.dinner_reminder_time?.substring(0, 5) || '18:30',
          achievementEmailsEnabled: data?.achievement_emails_enabled ?? true
        });

        setEmailSummarySettings({
          emailBadges: data?.email_badges ?? false,
          emailMilestones: data?.email_milestones ?? false,
          emailDuels: data?.email_duels ?? false,
          emailFriendActivity: data?.email_friend_activity ?? false
        });

        setNotifications(prev => ({
          ...prev,
          email: {
            ...prev?.email,
            weeklyReports: data?.email_digest_enabled ?? true,
            achievements: data?.achievement_emails_enabled ?? true
          },
          push: {
            ...prev?.push,
            mealReminders: data?.meal_reminders_enabled ?? false
          }
        }));
      }
    } catch (error) {
      console.error('Error loading email digest settings:', error);
    }
  };

  const handleToggle = (category, setting) => {
    setNotifications(prev => ({
      ...prev,
      [category]: {
        ...prev?.[category],
        [setting]: !prev?.[category]?.[setting]
      }
    }));
    setHasChanges(true);

    if (category === 'push' && setting === 'duelEvents') {
      handlePushNotificationToggle(!notifications?.push?.duelEvents);
    }
  };

  const handlePushNotificationToggle = async (enable) => {
    try {
      if (enable) {
        const permission = await pushNotificationService?.requestPermission();
        if (permission === 'granted') {
          await pushNotificationService?.subscribeToPush(user?.id);
          setPushSubscribed(true);
        } else {
          setNotifications(prev => ({
            ...prev,
            push: { ...prev?.push, duelEvents: false }
          }));
        }
      } else {
        await pushNotificationService?.unsubscribe(user?.id);
        setPushSubscribed(false);
      }
    } catch (error) {
      console.error('Error toggling push notifications:', error);
      setNotifications(prev => ({
        ...prev,
        push: { ...prev?.push, duelEvents: !enable }
      }));
    }
  };

  const handleEmailDigestChange = (field, value) => {
    setEmailDigestSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleEmailSummaryChange = (field, value) => {
    setEmailSummarySettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!user?.id) return;

    setLoading(true);
    setSaveSuccess(false);

    try {
      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({
          email_digest_enabled: emailDigestSettings?.emailDigestEnabled,
          weekly_digest_day: emailDigestSettings?.weeklyDigestDay,
          meal_reminders_enabled: emailDigestSettings?.mealRemindersEnabled,
          breakfast_reminder_time: emailDigestSettings?.breakfastReminderTime + ':00',
          lunch_reminder_time: emailDigestSettings?.lunchReminderTime + ':00',
          dinner_reminder_time: emailDigestSettings?.dinnerReminderTime + ':00',
          achievement_emails_enabled: emailDigestSettings?.achievementEmailsEnabled,
          email_badges: emailSummarySettings?.emailBadges,
          email_milestones: emailSummarySettings?.emailMilestones,
          email_duels: emailSummarySettings?.emailDuels,
          email_friend_activity: emailSummarySettings?.emailFriendActivity
        })
        ?.eq('id', user?.id);

      if (error) throw error;

      setHasChanges(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving notification settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    loadEmailDigestSettings();
    setNotifications({
      email: {
        dailyReminders: true,
        weeklyReports: true,
        achievements: true,
        goalUpdates: false,
        tips: true
      },
      push: {
        waterReminders: true,
        activityReminders: true,
        sleepReminders: true,
        mealReminders: false,
        achievements: true
      }
    });
    setHasChanges(false);
  };

  const ToggleSwitch = ({ checked, onChange, label, description }) => (
    <div className="flex items-center justify-between py-3">
      <div className="flex-1">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-foreground">{label}</span>
        </div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={onChange}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
          checked ? 'bg-primary' : 'bg-muted'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  return (
    <div className="bg-card rounded-xl p-6 elevation-2">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-10 h-10 bg-accent rounded-lg">
            <Icon name="Bell" size={20} color="white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Notification Settings</h2>
            <p className="text-sm text-muted-foreground">Manage how you receive updates and reminders</p>
          </div>
        </div>
        {hasChanges && (
          <div className="flex space-x-2">
            <Button variant="outline" onClick={handleReset} size="sm" disabled={loading}>
              Reset
            </Button>
            <Button variant="default" onClick={handleSave} size="sm" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
        {saveSuccess && (
          <div className="flex items-center space-x-2 text-success">
            <Icon name="CheckCircle" size={18} />
            <span className="text-sm font-medium">Settings saved!</span>
          </div>
        )}
      </div>
      <div className="space-y-8">
        {/* Email Notifications */}
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="Mail" size={18} className="text-primary" />
            <h3 className="text-lg font-medium text-foreground">Email Notifications</h3>
          </div>
          <div className="bg-muted/30 rounded-lg p-4 space-y-1">
            <ToggleSwitch
              checked={notifications?.email?.dailyReminders}
              onChange={() => handleToggle('email', 'dailyReminders')}
              label="Daily Reminders"
              description="Get daily motivation and goal reminders"
            />
            <ToggleSwitch
              checked={emailDigestSettings?.emailDigestEnabled}
              onChange={() => handleEmailDigestChange('emailDigestEnabled', !emailDigestSettings?.emailDigestEnabled)}
              label="Weekly Wellness Digest"
              description="Receive comprehensive weekly health summaries via email"
            />
            {emailDigestSettings?.emailDigestEnabled && (
              <div className="ml-6 mt-2 mb-3 p-3 bg-background rounded-lg border border-border">
                <label className="block text-sm font-medium text-foreground mb-2">Delivery Day</label>
                <select
                  value={emailDigestSettings?.weeklyDigestDay}
                  onChange={(e) => handleEmailDigestChange('weeklyDigestDay', e?.target?.value)}
                  className="w-full px-3 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="Monday">Monday</option>
                  <option value="Tuesday">Tuesday</option>
                  <option value="Wednesday">Wednesday</option>
                  <option value="Thursday">Thursday</option>
                  <option value="Friday">Friday</option>
                  <option value="Saturday">Saturday</option>
                  <option value="Sunday">Sunday</option>
                </select>
              </div>
            )}
            <ToggleSwitch
              checked={emailDigestSettings?.achievementEmailsEnabled}
              onChange={() => handleEmailDigestChange('achievementEmailsEnabled', !emailDigestSettings?.achievementEmailsEnabled)}
              label="Achievement Celebrations"
              description="Receive email notifications when you unlock achievements"
            />
            <ToggleSwitch
              checked={notifications?.email?.goalUpdates}
              onChange={() => handleToggle('email', 'goalUpdates')}
              label="Goal Updates"
              description="Notifications when you reach your daily goals"
            />
            <ToggleSwitch
              checked={notifications?.email?.tips}
              onChange={() => handleToggle('email', 'tips')}
              label="Health Tips"
              description="Personalized wellness tips and advice"
            />
          </div>
        </div>

        {/* Email Delivery Summaries */}
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="Send" size={18} className="text-indigo-500" />
            <h3 className="text-lg font-medium text-foreground">Email Delivery Summaries</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-3">Choose which activity types trigger an email summary to your inbox.</p>
          <div className="bg-muted/30 rounded-lg p-4 space-y-1">
            <ToggleSwitch
              checked={emailSummarySettings?.emailBadges}
              onChange={() => handleEmailSummaryChange('emailBadges', !emailSummarySettings?.emailBadges)}
              label="Badge Earned"
              description="Get an email when you unlock a new badge"
            />
            <ToggleSwitch
              checked={emailSummarySettings?.emailMilestones}
              onChange={() => handleEmailSummaryChange('emailMilestones', !emailSummarySettings?.emailMilestones)}
              label="Milestone Reached"
              description="Get an email when you hit a fitness or wellness milestone"
            />
            <ToggleSwitch
              checked={emailSummarySettings?.emailDuels}
              onChange={() => handleEmailSummaryChange('emailDuels', !emailSummarySettings?.emailDuels)}
              label="Duel Updates"
              description="Get emails for duel invites, starts, proof submissions, and results"
            />
            <ToggleSwitch
              checked={emailSummarySettings?.emailFriendActivity}
              onChange={() => handleEmailSummaryChange('emailFriendActivity', !emailSummarySettings?.emailFriendActivity)}
              label="Friend Activity"
              description="Get an email when friends earn badges, hit milestones, or log workouts"
            />
          </div>
        </div>

        {/* Push Notifications */}
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="Smartphone" size={18} className="text-secondary" />
            <h3 className="text-lg font-medium text-foreground">Push Notifications</h3>
          </div>
          <div className="bg-muted/30 rounded-lg p-4 space-y-1">
            <ToggleSwitch
              checked={notifications?.push?.waterReminders}
              onChange={() => handleToggle('push', 'waterReminders')}
              label="Water Reminders"
              description="Stay hydrated with regular water intake reminders"
            />
            <ToggleSwitch
              checked={notifications?.push?.activityReminders}
              onChange={() => handleToggle('push', 'activityReminders')}
              label="Activity Reminders"
              description="Get reminded to move and stay active"
            />
            <ToggleSwitch
              checked={notifications?.push?.sleepReminders}
              onChange={() => handleToggle('push', 'sleepReminders')}
              label="Sleep Reminders"
              description="Bedtime reminders for better sleep hygiene"
            />
            <ToggleSwitch
              checked={emailDigestSettings?.mealRemindersEnabled}
              onChange={() => handleEmailDigestChange('mealRemindersEnabled', !emailDigestSettings?.mealRemindersEnabled)}
              label="Meal Reminder Emails"
              description="Receive email reminders for breakfast, lunch, and dinner"
            />
            {emailDigestSettings?.mealRemindersEnabled && (
              <div className="ml-6 mt-2 mb-3 p-3 bg-background rounded-lg border border-border space-y-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Breakfast Time</label>
                  <input
                    type="time"
                    value={emailDigestSettings?.breakfastReminderTime}
                    onChange={(e) => handleEmailDigestChange('breakfastReminderTime', e?.target?.value)}
                    className="w-full px-3 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Lunch Time</label>
                  <input
                    type="time"
                    value={emailDigestSettings?.lunchReminderTime}
                    onChange={(e) => handleEmailDigestChange('lunchReminderTime', e?.target?.value)}
                    className="w-full px-3 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Dinner Time</label>
                  <input
                    type="time"
                    value={emailDigestSettings?.dinnerReminderTime}
                    onChange={(e) => handleEmailDigestChange('dinnerReminderTime', e?.target?.value)}
                    className="w-full px-3 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            )}
            <ToggleSwitch
              checked={notifications?.push?.achievements}
              onChange={() => handleToggle('push', 'achievements')}
              label="Achievement Alerts"
              description="Instant notifications for completed goals"
            />
            <ToggleSwitch
              checked={notifications?.push?.duelEvents}
              onChange={() => handleToggle('push', 'duelEvents')}
              label="Duel Events"
              description="Get notified when duels start, proofs are submitted, and winners are declared"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettingsSection;